import { useCallback } from 'react';
import type { ResumeDraft, ResumeSectionId, ResumeVersion, TemplateId } from '../../lib/resume/types';
import {
  RESUME_SECTION_LABEL,
  TEMPLATE_DESCRIPTION,
  TEMPLATE_IDS,
  TEMPLATE_LABEL,
} from '../../lib/resume/types';
import {
  hiddenSections,
  moveEntry,
  moveSection,
  removeEntry,
  removeSection,
  restoreSection,
  setName,
  setSummary,
  setTargetRole,
  setTemplate,
  updateCertification,
  updateEducation,
  updateExperience,
  updateProject,
  updateSkill,
} from '../../lib/resume/edit';
import { ResumePreview } from './ResumePreview';
import { VersionPanel } from './VersionPanel';
import { Badge, Button, Card, Notice, SectionHeader, TextField } from '../ui';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeOffIcon,
  LayersIcon,
  RefreshIcon,
} from '../ui/icons';
import {
  CertificationsEditor,
  EducationEditor,
  ExperienceEditor,
  ProjectsEditor,
  SkillsEditor,
  SummaryEditor,
} from './ResumeSectionEditors';

interface Props {
  draft: ResumeDraft;
  onChange: (next: ResumeDraft) => void;
  versions: ResumeVersion[];
  onSaveVersion: (name: string) => void;
  onRestoreVersion: (versionId: string) => void;
  onRebuildFromProfile: () => void;
}

export function ResumeBuilder({
  draft,
  onChange,
  versions,
  onSaveVersion,
  onRestoreVersion,
  onRebuildFromProfile,
}: Props) {
  const hidden = hiddenSections(draft);

  const moveEntryIn = useCallback(
    (section: 'experience' | 'education' | 'skills' | 'certifications' | 'projects') =>
      (id: string, delta: -1 | 1) => onChange(moveEntry(draft, section, id, delta)),
    [draft, onChange],
  );

  const removeEntryIn = useCallback(
    (section: 'experience' | 'education' | 'skills' | 'certifications' | 'projects') =>
      (id: string) => onChange(removeEntry(draft, section, id)),
    [draft, onChange],
  );

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-5">
        <Card aria-labelledby="resume-header-heading">
          <SectionHeader
            id="resume-header-heading"
            eyebrow="Editor"
            title="Resume draft"
            action={<Badge tone="navy">{TEMPLATE_LABEL[draft.template]} template</Badge>}
          />
          <div className="mt-4">
            <Notice tone="warning">
              <div>
                This draft and its versions live in browser memory only. Refreshing
                or closing the page loses them. The master profile is never changed
                by editing here.
              </div>
            </Notice>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <TextField
              label="Draft name"
              value={draft.name}
              onChange={(v) => onChange(setName(draft, v))}
            />
            <TextField
              label="Target role"
              value={draft.targetRole}
              onChange={(v) => onChange(setTargetRole(draft, v))}
            />
          </div>

          <fieldset className="mt-5">
            <legend className="text-sm font-semibold text-ink">Template</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {TEMPLATE_IDS.map((template: TemplateId) => {
                const selected = draft.template === template;
                return (
                  <label
                    key={template}
                    className={[
                      'flex cursor-pointer flex-col gap-1.5 rounded-lg border p-3 text-sm transition-colors',
                      selected
                        ? 'border-accent bg-accent-soft shadow-card'
                        : 'border-line bg-surface-sunken hover:border-navy/40',
                    ].join(' ')}
                  >
                    <span className="flex items-center gap-2 font-semibold text-ink">
                      <input
                        type="radio"
                        name="resume-template"
                        value={template}
                        checked={selected}
                        onChange={() => onChange(setTemplate(draft, template))}
                        className="h-4 w-4 text-accent"
                      />
                      {TEMPLATE_LABEL[template]}
                    </span>
                    <span className="text-xs leading-relaxed text-ink-faint">
                      {TEMPLATE_DESCRIPTION[template]}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-5">
            <Button
              variant="secondary"
              icon={<RefreshIcon />}
              onClick={onRebuildFromProfile}
            >
              Rebuild draft from profile
            </Button>
          </div>
        </Card>

        <VersionPanel versions={versions} onSave={onSaveVersion} onRestore={onRestoreVersion} />

        <SectionOrderControl draft={draft} onChange={onChange} hidden={hidden} />

        {draft.sectionOrder.map((section) => (
          <Card key={section} role="region" aria-label={`Edit ${RESUME_SECTION_LABEL[section]}`}>
            <SectionHeader
              title={RESUME_SECTION_LABEL[section]}
              action={
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<EyeOffIcon />}
                  onClick={() => onChange(removeSection(draft, section))}
                >
                  Remove section from draft
                </Button>
              }
            />
            <div className="mt-4">
              {section === 'summary' && (
                <SummaryEditor
                  draft={draft}
                  setSummary={(value) => onChange(setSummary(draft, value))}
                />
              )}
              {section === 'experience' && (
                <ExperienceEditor
                  draft={draft}
                  update={(id, patch) => onChange(updateExperience(draft, id, patch))}
                  move={moveEntryIn('experience')}
                  remove={removeEntryIn('experience')}
                />
              )}
              {section === 'education' && (
                <EducationEditor
                  draft={draft}
                  update={(id, patch) => onChange(updateEducation(draft, id, patch))}
                  move={moveEntryIn('education')}
                  remove={removeEntryIn('education')}
                />
              )}
              {section === 'skills' && (
                <SkillsEditor
                  draft={draft}
                  update={(id, patch) => onChange(updateSkill(draft, id, patch))}
                  move={moveEntryIn('skills')}
                  remove={removeEntryIn('skills')}
                />
              )}
              {section === 'certifications' && (
                <CertificationsEditor
                  draft={draft}
                  update={(id, patch) => onChange(updateCertification(draft, id, patch))}
                  move={moveEntryIn('certifications')}
                  remove={removeEntryIn('certifications')}
                />
              )}
              {section === 'projects' && (
                <ProjectsEditor
                  draft={draft}
                  update={(id, patch) => onChange(updateProject(draft, id, patch))}
                  move={moveEntryIn('projects')}
                  remove={removeEntryIn('projects')}
                />
              )}
            </div>
          </Card>
        ))}
      </div>

      <div className="xl:sticky xl:top-24 xl:self-start">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-soft">
            <LayersIcon className="h-4 w-4" />
            Live preview
          </h2>
          <Badge tone="neutral">Print-style</Badge>
        </div>
        <div className="overflow-auto rounded-lg border border-line bg-surface-sunken p-4 shadow-card sm:p-6">
          <ResumePreview draft={draft} />
        </div>
      </div>
    </div>
  );
}

function SectionOrderControl({
  draft,
  onChange,
  hidden,
}: {
  draft: ResumeDraft;
  onChange: (next: ResumeDraft) => void;
  hidden: ResumeSectionId[];
}) {
  return (
    <Card aria-labelledby="section-order-heading">
      <SectionHeader
        id="section-order-heading"
        title="Section order"
        description="Reorder how sections appear on the resume, or hide one without deleting its data."
      />

      <ul className="mt-4 space-y-2" aria-label="Visible sections">
        {draft.sectionOrder.map((section, index) => (
          <li
            key={section}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-surface-sunken px-3 py-2"
          >
            <span className="text-sm font-medium text-ink">
              {RESUME_SECTION_LABEL[section]}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                aria-label="Move up"
                icon={<ArrowUpIcon />}
                disabled={index === 0}
                onClick={() => onChange(moveSection(draft, section, -1))}
              />
              <Button
                size="sm"
                variant="ghost"
                aria-label="Move down"
                icon={<ArrowDownIcon />}
                disabled={index === draft.sectionOrder.length - 1}
                onClick={() => onChange(moveSection(draft, section, 1))}
              />
              <Button
                size="sm"
                variant="ghost"
                icon={<EyeOffIcon />}
                onClick={() => onChange(removeSection(draft, section))}
              >
                Hide
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {hidden.length > 0 ? (
        <div className="mt-4 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-ink">Hidden sections</h3>
          <ul className="mt-2 space-y-2" aria-label="Hidden sections">
            {hidden.map((section) => (
              <li
                key={section}
                className="flex items-center justify-between gap-2 rounded-md border border-dashed border-line-strong bg-surface-sunken px-3 py-2"
              >
                <span className="text-sm text-ink-soft">{RESUME_SECTION_LABEL[section]}</span>
                <Button
                  size="sm"
                  variant="quiet"
                  onClick={() => onChange(restoreSection(draft, section))}
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
